import React from 'react';
import { motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { briefAnalogy, briefMeta, briefSections } from '../../data/brief';
const EASE = [0.23, 1, 0.32, 1] as const;
interface BriefOverlayProps {
  onClose: () => void;
}
export function BriefOverlay({
  onClose
}: BriefOverlayProps) {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.18,
    ease: EASE
  }} className="fixed inset-0 z-50 flex items-center justify-center bg-ink/70 px-6 py-8" role="dialog" aria-modal="true" aria-label={briefMeta.title} data-id="element-25" data-mp="1hdtb2n442fnp">
      <motion.div initial={{
      opacity: 0,
      y: 10,
      scale: 0.98
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.98
    }} transition={{
      duration: 0.24,
      ease: EASE
    }} className="flex max-h-full w-full max-w-3xl flex-col overflow-hidden rounded-[28px] bg-paper shadow-soft" data-id="element-26" data-mp="13j0oj4pszz7m">
        <div className="flex items-start justify-between gap-6 bg-navy px-8 py-6" data-id="element-27" data-mp="3b1mj2ul8v8te">
          <div data-id="element-28" data-mp="3g07ln0nyy4l0">
            <p className="flex items-center gap-2.5 text-[10px] uppercase tracking-micro text-mint" data-id="element-29" data-mp="3fzo9k0l02aew">
              <span className="h-1.5 w-1.5 rounded-full bg-mint" aria-hidden="true" data-id="element-30" data-mp="3kz0xyprirpjl" />
              {briefMeta.eyebrow}
            </p>
            <h2 className="mt-2 font-display text-[26px] font-semibold leading-tight tracking-tight text-paper" data-id="element-31" data-mp="3p0cgycfw1cit">
              {briefMeta.title}
            </h2>
            <p className="mt-1.5 text-[12px] text-sky/70" data-id="element-32" data-mp="2x96is1jq9ow6">{briefMeta.subtitle}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close strategic brief" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-sky/25 text-sky/80 transition-colors duration-150 hover:border-mint/50 hover:text-mint focus:outline-none focus-visible:ring-2 focus-visible:ring-mint" data-id="element-33" data-mp="31aru3igm5v49">
            <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-34" data-mp="3gwd20yds6j5m" />
          </button>
        </div>

        <div className="overflow-y-auto px-8 py-7" data-id="element-35" data-mp="3bxjl0vzaca0p">
          <section className="rounded-2xl border-l-2 border-mint bg-sky/40 px-6 py-5" data-id="element-36" data-mp="3bx0f6dmwtc31">
            <p className="text-[10px] uppercase tracking-micro text-mut" data-id="element-37" data-mp="3ql4ncnoc657k">Strategic idea</p>
            <p className="mt-2 font-display text-[24px] font-semibold leading-tight tracking-tight text-navy" data-id="element-38" data-mp="3loe7o1gg1bmo">
              {briefMeta.idea}
            </p>
            <p className="mt-3 text-[13.5px] leading-relaxed text-navy" data-id="element-39" data-mp="2synsdp32s3oz">{briefMeta.ideaSupport}</p>
          </section>

          <section className="mt-5 overflow-hidden rounded-2xl bg-navy px-6 py-6" data-id="element-40" data-mp="36yyjmt8arkao">
            <p className="text-[10px] uppercase tracking-micro text-mint" data-id="element-41" data-mp="32wv7e2dt0boc">{briefAnalogy.label}</p>
            <p className="mt-2 font-display text-[22px] font-semibold leading-tight tracking-tight text-paper" data-id="element-42" data-mp="m87sk8eud5yo">
              {briefAnalogy.lead}
            </p>

            {briefAnalogy.body.map(p => <p key={p} className="mt-3 text-[13.5px] leading-relaxed text-sky/75" data-id="element-43" data-mp="pqf0rf9veij4">
                {p}
              </p>)}

            <div className="mt-5 grid gap-3 sm:grid-cols-2" data-id="element-44" data-mp="uq00hrwv6uww">
              {briefAnalogy.exchange.map(item => <div key={item.side} className="border-t border-sky/20 pt-3" data-id="element-45" data-mp="ftsjt1ko0bmd">
                  <p className="text-[9px] uppercase tracking-micro text-mint" data-id="element-46" data-mp="kru0jfumbyuo">{item.side}</p>
                  <p className="mt-1.5 text-[13px] leading-snug text-paper" data-id="element-47" data-mp="18hoe5zmrhrfz">{item.value}</p>
                </div>)}
            </div>

            <p className="mt-5 border-l-2 border-mint pl-4 text-[13.5px] leading-relaxed text-paper" data-id="element-48" data-mp="1dhkgluft9atf">
              {briefAnalogy.implication}
            </p>
          </section>

          <div className="mt-8 space-y-8" data-id="element-49" data-mp="13o3pwzaz0btz">
            {briefSections.map(section => <section key={section.number} data-id="element-50" data-mp="1rdxwiz4p545s">
                <div className="flex items-baseline gap-3 border-b border-line pb-2" data-id="element-51" data-mp="1w5xvopcxzt1b">
                  <span className="font-display text-[11px] font-bold tabular-nums tracking-micro text-blue" data-id="element-52" data-mp="8hw9c6wy7xq0">
                    {section.number}
                  </span>
                  <h3 className="text-[11px] uppercase tracking-micro text-navy" data-id="element-53" data-mp="3hi1aovcknrz">
                    {section.title}
                  </h3>
                </div>

                {section.lead && <p className="mt-3 font-display text-[17px] leading-snug text-navy" data-id="element-54" data-mp="3prubmqrk3ufq">
                    {section.lead}
                  </p>}

                {section.body?.map(p => <p key={p} className="mt-3 text-[13.5px] leading-relaxed text-mut" data-id="element-55" data-mp="mbdrpodwf4eg">
                    {p}
                  </p>)}

                {section.list && <ul className="mt-3 space-y-2" data-id="element-56" data-mp="cj7sbtlbhkp8">
                    {section.list.map(item => <li key={item} className="flex gap-3 text-[13.5px] leading-relaxed text-navy" data-id="element-57" data-mp="1a06kxd34yy5d">
                        <span className="mt-[8px] h-1 w-1 shrink-0 rounded-full bg-blue" aria-hidden="true" data-id="element-58" data-mp="15628ieb3pxww" />
                        {item}
                      </li>)}
                  </ul>}
              </section>)}
          </div>
        </div>
      </motion.div>
    </motion.div>;
}